import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error
  const polls = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      polls.current += 1;
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          await checkAuth();
          setState("success");
          return;
        }
        if (["expired", "failed"].includes(res.data.payment_status)) {
          setState("error");
          return;
        }
      } catch (e) {}
      if (polls.current >= 12) {
        setState("error");
        return;
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [checkAuth]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7] px-6" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center omni-rise">
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-[#2D6A4F] animate-spin mx-auto" />
            <h1 className="font-heading text-2xl font-bold text-[#171716] mt-6">Confirming your payment…</h1>
            <p className="text-[#5F5F5B] mt-2">This only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="h-16 w-16 rounded-2xl bg-[#EAF3EE] flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-9 w-9 text-[#2D6A4F]" />
            </div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#1F2421] text-white px-3 py-1 mt-6">
              <Sparkles className="h-3.5 w-3.5 text-[#7FD3AC]" />
              <span className="text-xs font-semibold tracking-wide">OMNIAI PRO</span>
            </div>
            <h1 className="font-heading text-3xl font-bold text-[#171716] mt-4">You're all set!</h1>
            <p className="text-[#5F5F5B] mt-2">All 16 models, image generation and Compare Mode are now unlocked.</p>
            <button onClick={() => navigate("/")} data-testid="go-to-app-button" className="mt-8 rounded-full bg-[#1F2421] text-white px-6 py-3 font-medium hover:bg-[#2D6A4F]" style={{ transition: "background-color 0.2s ease" }}>
              Start using Pro
            </button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="h-16 w-16 rounded-2xl bg-red-50 flex items-center justify-center mx-auto">
              <XCircle className="h-9 w-9 text-red-500" />
            </div>
            <h1 className="font-heading text-2xl font-bold text-[#171716] mt-6">We couldn't confirm your payment</h1>
            <p className="text-[#5F5F5B] mt-2">If you were charged, your Pro access will activate shortly. Otherwise, please try again.</p>
            <button onClick={() => navigate("/")} className="mt-8 rounded-full border border-[#E6E6DF] bg-white px-6 py-3 font-medium text-[#171716] hover:border-[#CECEBE]" style={{ transition: "border-color 0.2s ease" }}>
              Back to OmniAI
            </button>
          </>
        )}
      </div>
    </div>
  );
}
